import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../apiConfig";
import { Trophy, Info } from "lucide-react";
import { motion } from "framer-motion";

function LeagueTable({ id }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/divisionLeague/${id}`).then((res) => {
            setRows(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [id]);

    const FONT_DISPLAY = "'Barlow Condensed', sans-serif";
    const FONT_BODY = "'Rajdhani', sans-serif";
    const accentYellow = '#E7C424';

    const styles = {
        container: {
            width: '100%',
            maxWidth: '1280px',
            background: 'linear-gradient(180deg, #0A1172 0%, #040960 100%)',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
        },
        tableWrapper: {
            width: '100%',
            overflowX: 'auto',
            padding: '0',
            boxSizing: 'border-box',
            WebkitOverflowScrolling: 'touch',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: isMobile ? '700px' : '1000px',
        },
        th: {
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            fontSize: isMobile ? '13px' : '15px',
            color: 'rgba(255,255,255,0.6)',
            textAlign: 'center',
            padding: isMobile ? '12px 6px' : '16px 12px',
            background: 'rgba(0,0,0,0.2)',
            borderBottom: '1px solid rgba(255,255,255,0.15)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        td: {
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: isMobile ? '14px' : '17px',
            color: '#FFFFFF',
            textAlign: 'center',
            padding: isMobile ? '12px 6px' : '14px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
        },
        teamCol: {
            textAlign: 'left',
            paddingLeft: isMobile ? '16px' : '24px',
            width: isMobile ? '160px' : '320px',
            position: 'sticky',
            left: 0,
            background: '#070D7C', // Fallback for sticky
            zIndex: 1,
        },
        footer: {
            width: '100%',
            background: 'rgba(0,0,0,0.3)',
            padding: '16px 24px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '12px 20px',
            boxSizing: 'border-box',
            borderTop: '1px solid rgba(255,255,255,0.1)',
        },
        legendItem: {
            fontFamily: FONT_BODY,
            fontWeight: 500,
            fontSize: '12px',
            color: 'rgba(255,255,255,0.5)',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        }
    };

    const legendItems = [
        { key: 'P', label: 'Played' },
        { key: 'W', label: 'Won' },
        { key: 'L', label: 'Loss' },
        { key: 'C', label: 'Cancelled' },
        { key: 'A', label: 'Abandoned' },
        { key: 'ICN', label: 'Team Conceded' },
        { key: 'WCN', label: 'Opposition Conceded' },
        { key: 'T', label: 'Tied' },
        { key: 'BP', label: 'Bonus' },
        { key: 'Pen', label: 'Penalty' },
        { key: 'NRR', label: 'NRR' },
        { key: 'Pts', label: 'Points' }
    ];

    return (
        <div style={styles.container}>
            <div style={styles.tableWrapper}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.th, ...styles.teamCol }}>Team</th>
                            <th style={styles.th}>P</th>
                            <th style={styles.th}>W</th>
                            <th style={styles.th}>L</th>
                            <th style={styles.th}>C</th>
                            <th style={styles.th}>A</th>
                            <th style={styles.th}>ICN</th>
                            <th style={styles.th}>WCN</th>
                            <th style={styles.th}>T</th>
                            <th style={styles.th}>BP</th>
                            <th style={styles.th}>Pen</th>
                            <th style={styles.th}>NRR</th>
                            <th style={{...styles.th, color: accentYellow}}>Pts</th>
                        </tr>
                    </thead>
                    <tbody style={{opacity: loading ? 0.5 : 1, transition: 'opacity 0.3s'}}>
                        {rows.length > 0 ? rows.map((item, i) => (
                            <motion.tr 
                                key={i}
                                whileHover={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                                style={{background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'}}
                            >
                                <td style={{ ...styles.td, ...styles.teamCol, color: i === 0 ? accentYellow : '#fff' }}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                        {i === 0 && <Trophy size={14} color={accentYellow} />}
                                        {item.team_name}
                                    </div>
                                </td>
                                <td style={styles.td}>{item.p}</td>
                                <td style={styles.td}>{item.w}</td>
                                <td style={styles.td}>{item.i}</td>
                                <td style={styles.td}>{item.c}</td>
                                <td style={styles.td}>{item.a}</td>
                                <td style={styles.td}>{item.lcn}</td>
                                <td style={styles.td}>{item.wcn}</td>
                                <td style={styles.td}>{item.t}</td>
                                <td style={styles.td}>{item.BP}</td>
                                <td style={styles.td}>{item.Pen}</td>
                                <td style={styles.td}>{item.NRR}</td>
                                <td style={{ ...styles.td, color: accentYellow, fontWeight: 900 }}>{item.Pts}</td>
                            </motion.tr>
                        )) : (
                            <tr>
                                <td colSpan="13" style={{ ...styles.td, padding: '48px', color: 'rgba(255,255,255,0.4)', fontFamily: FONT_BODY }}>
                                    {loading ? 'Loading league data...' : 'No data available for this division.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Legend Footer */}
            <div style={styles.footer}>
                {legendItems.map((item, index) => (
                    <div key={index} style={styles.legendItem}>
                        <span style={{ fontWeight: 800, color: 'rgba(255,255,255,0.8)' }}>{item.key}</span>
                        <span>{item.label}</span>
                        {index < legendItems.length - 1 && <span style={{marginLeft: '12px', opacity: 0.2}}>|</span>}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default LeagueTable;